import {
  ANSWER_FACET, aliasDriverSpace, aliasUncoveredFrom, axisBlockedBy,
  canLeadScenarios, deriveGraph, driverSpacesFor, drivenLinkReach, formatDriverAddress,
  parseDriverAddress, collapseDrivenAxis, versionAxisOwner,
} from '@agavi/blocks';
import { Anchor, ChevronDown, Link2, OctagonAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { aliasDisplayName, useAnswerRefScope } from './answer-ref-context';
import { answerOptionSummary, questionSummary, REACH_ICON, REACH_LABEL } from './answer-ref-picker-menu';
import { ANSWERABLE_BLOCK_TYPES, BLOCK_META, VERSIONED_BLOCK_TYPES } from './block-library';
import { FlowPickerMenu, type PickerSection } from './flow-picker-menu';
import { REFUSAL_REASON } from './use-driver-rows';
import { useDrivenAllowed } from './use-recall-affordance';

import type { Block, BlockType } from '@/types/experience';
import type { AxisOwner } from '@agavi/blocks';

import { useCapability } from '@/hooks/use-capabilities';


/**
 * Pick the display block whose variant decides this block's answer — see
 * `docs/linked-scenarios.md` §4.3.
 *
 * BACKWARDS, from the follower. Linking from the leader was rejected on ordering: a scenario is
 * often the FIRST block added, so at the moment you are looking at it there is nothing yet to
 * link to. From here the menu is never empty by the time it matters.
 *
 * It renders NOTHING when the task holds no linkable leader. Every block growing a permanently
 * empty control would advertise a feature most tasks never use, and the rail already has enough
 * going on — the control appears when a display block gains a bank, which is the moment it
 * becomes true that something can be followed.
 *
 * ## It names the BLOCK, at both ends
 *
 * The first build labelled the trigger "Scenario" and, once linked, "Following", with the open
 * list showing bare block names. Every part of that was wrong in the same way, and the owner
 * reported all three at once:
 *
 *   - "Scenario" on the trigger read as *pick which scenario*, so an option called "Text 2"
 *     looked like scenario number two rather than a block called Text 2
 *   - "Following" says a link exists and not what it points at, which is the only part worth
 *     the screen space
 *   - a bare name does not read as a block, so nothing said that the thing being chosen is an
 *     object in this task
 *
 * So: the block's own library glyph beside each name in the list, and the leader's name on the
 * trigger once chosen. The control says what it is connected to, which is the whole question it
 * answers.
 *
 * `Link2`, a plain chain. `Waypoints` was tried first on the theory that a chain reads as
 * "hyperlink" next to a rich-text toolbar; in place its connected dots read as "random
 * lightning" instead, which is worse — an unrecognisable glyph says nothing at all, where a
 * slightly over-loaded one at least says *connection*. The toolbar beside it has no link button
 * to be confused with, so the risk was theoretical and the cost was not.
 */
/**
 * ## Two relationships, one control
 *
 * A second kind of link arrived and shares this menu rather than adding a button beside it.
 * They are genuinely different — one coordinates with a block in THIS task, the other varies
 * with what a student answered in an EARLIER one — so they are separate sections with their
 * own headings, which is where there is room to say which is which. Two icons side by side
 * could not.
 *
 * Scenarios are listed first: they are the older idea, the commoner one, and the one confined
 * to what is already on screen.
 *
 * They are also MUTUALLY EXCLUSIVE, and the menu enforces that by being one control. A block
 * follows a scenario, or is driven by an answer, or neither — two selectors over one answer
 * setup is a product space, which is the thing versions already refuse.
 *
 * ## A blocked section is ONE greyed row, not a greyed list
 *
 * There are two reasons a row can be unavailable and they want different shapes. A block that
 * cannot DRIVE — multi-select, ordering — is greyed in place, because the question it answers
 * is "why is my MCQ not here" and only that row can answer it. A section blocked because the
 * axis is already spoken for is one reason for the whole section, and saying it N times is N
 * copies of one sentence. So the section collapses to a single row carrying its heading and
 * the cause.
 *
 * It also has to be a row rather than a note on the heading: `SelectMenu`'s group headings are
 * `aria-hidden`, so a reason put there is a reason a screen reader never hears. An option is
 * announced, disabled and all — which is why that component uses `aria-disabled`.
 *
 * ## `orphaned` always renders, even with nothing to offer
 *
 * A block whose driver could not be resolved on paste keeps its versions and loses its link,
 * and the early return below would hide the only control that can fix it. The cause is
 * `versionAxisOwner`'s fourth state, read off the versions' own row ids.
 */
/**
 * Why a section is unavailable, keyed by what owns the axis.
 *
 * Written out rather than built as `blocks.scenario.blocked.${owner}`. The app has a budget of
 * fifty constructed `t()` keys and this took it to fifty-one, which is the invariant doing its
 * job: a key spelled in full is one the i18n check can verify, and the two that can never be a
 * reason say so by being empty rather than by not existing.
 */
/**
 * WHY A BLOCK CANNOT BE DRIVEN FROM, keyed by the reason rather than one sentence for all.
 *
 * "Its answers cannot be listed" covered three different facts, and was wrong about the
 * commonest: a teacher whose MCQ has no options yet read it as a property of the TYPE and went
 * looking for another block, when the fix was to write the options. `driverRefusal` tells the
 * permanent cases from the ones that clear themselves; this says each in the teacher's words.
 *
 * NO "Not available" PREFIX (owner). The row is greyed and `aria-disabled`, so both the sight
 * of it and the screen reader already say unavailable — a prefix spends the first two words
 * restating that instead of naming the cause.
 *
 * Spelled out, like `BLOCKED_REASON` below and for its reason: a constructed
 * `blocks.scenario.refusal.${r}` is a key the i18n check cannot verify.
 */
/* Lives in `use-driver-rows`, because the ISSUE LIST says the same sentences about an
   alias member and a second copy is two wordings of one refusal. */

const BLOCKED_REASON: Record<AxisOwner | 'unbanked', string> = {
  none: '',
  drawn: 'blocks.scenario.blocked.drawn',
  scenario: 'blocks.scenario.blocked.scenario',
  driven: 'blocks.scenario.blocked.driven',
  orphaned: 'blocks.scenario.blocked.orphaned',
  /* Not an axis owner: the block has no version machinery for an axis to live in. */
  unbanked: 'blocks.scenario.blocked.unbanked',
};

/**
 * THE DRIVEN HALF, BOTH PIECES OR NEITHER — a union rather than two optional props.
 *
 * `emit` writes the link and `applyVersion` is what collapses the bank when the link is
 * dropped to None. A caller with one and not the other compiles happily and then does nothing
 * at the moment it matters: the teacher picks None, no collapse is written, and the block sits
 * on a driven bank whose driver is gone. That is the same silent half-wiring the backend's
 * `remapDrivenBy` is written against — both halves or neither — so it is said here in the type
 * instead of guarded at the one call site that would notice.
 *
 * `applyVersion` is the block's own `apply…Version`, the arrangement
 * `useDrivenVersionProjection` has with `addVersion`. Every block has exactly one, signature
 * `(data, versionId) => data`, differing only in the line that re-keys its own answer space —
 * `applyOptionsVersion` for the three option blocks, `applyScaleVersion`, `applyFitbVersion`
 * and so on — which is why it is a prop and not something this file can derive.
 *
 * Absent together where the caller cannot offer a cross-task link at all, and then there is no
 * driven link to make or to drop.
 */
type DrivenHalf =
  | {
    emit: (data: Record<string, unknown>) => void;
    applyVersion: (data: never, versionId: string | undefined) => unknown;
  }
  | { emit?: undefined; applyVersion?: undefined };

export function ScenarioLinkMenu({ block, blocks, value, onChange, emit, applyVersion }: {
  /**
   * THE BLOCK ITSELF, so the driven half is owned here and not repeated at every mount.
   *
   * It was four props across four call sites — `useScenarioFollower` and the three blocks
   * that build their own copy of this menu — reading and writing the same field in the same
   * two lines. MCQ and Dropdown shipped a commit behind everything else for exactly that
   * reason: the wiring was a thing each site had to remember rather than a thing the control
   * does. The scenario half still takes its value and callback, because that one genuinely
   * differs — each block owns a `linkToLeader` that knows its own row type.
   */
  block: Block;
  blocks: Block[] | undefined;
  value: string | undefined;
  onChange: (followsBlockId: string | undefined) => void;
} & DrivenHalf) {
  const selfId = block.id;
  const drivenBy = (block.data as { drivenBy?: string } | undefined)?.drivenBy;
  const onDrivenChange = emit
    ? (next: string | undefined) => emit({ ...block.data, drivenBy: next })
    : undefined;
  const { t } = useTranslation();
  /*
   * THE TASK LIST COMES FROM THE RECALL SCOPE, not from eight callers threading it.
   *
   * It is the same provider the chips read, present wherever references are authorable and
   * absent in Preview — which is exactly where a cross-task link should not be offerable
   * either. Taking it as a prop would have meant the same two lines in every block that
   * mounts this, and one of them eventually not getting them.
   */
  const scope = useAnswerRefScope();
  const tasks = scope?.tasks;
  const currentTaskId = scope?.currentTaskId;

  /* The one place the three-way exclusivity is decided. `+ Version` reads the same rule from
     `isDrivenAxis`, which is the half of it a pager can see. */
  const owner = versionAxisOwner(block.data as Parameters<typeof versionAxisOwner>[0]);
  const blockedScenario = axisBlockedBy(owner, 'scenario');
  /*
   * AND A BLOCK WITH NO BANK CANNOT BE DRIVEN AT ALL — a driven axis IS a bank, selected by an
   * answer instead of a draw, so there is nothing for the rows to be projected onto. Rotation
   * is the only one, and it is parity debt rather than a decision; see `VERSIONED_BLOCK_TYPES`.
   *
   * Said rather than silently ignored. Without this the link stores, the projection finds no
   * version machinery, and the teacher is left looking at a control that did nothing.
   */
  const unbanked = !VERSIONED_BLOCK_TYPES.includes(block.type as BlockType);
  const blockedDriven = unbanked ? 'unbanked' : axisBlockedBy(owner, 'driven');

  /*
   * A leader is a DISPLAY block with at least two variants. `canLeadScenarios` draws the line at
   * two because `rollVersions` will not draw from a shorter bank — a one-variant leader never
   * varies, so offering it would be a link that silently does nothing.
   *
   * No chaining in v1 (§2.4): a block that already follows something cannot also be followed, so
   * it is filtered out rather than left to make a cycle.
   */
  /*
   * THE SCENARIO HALF ONLY — the driven half below is Answer Recall's and is NOT gated here.
   *
   * This menu is two relationships in one control, and they belong to two different
   * capabilities. Gating the component would take the recall sections with it, so the gate
   * lands on the leaders list: empty it and the scenario section does not build, the early
   * return below counts it as nothing to offer, and everything driven is untouched.
   *
   * A block ALREADY FOLLOWING keeps the section however the capability stands. It is the only
   * way to see what it follows or to unlink it, and hiding it would leave a block coordinating
   * its answers with a scenario and nothing on screen saying so — see `useExtraBlockSetting`
   * for the same rule stated for a scalar setting.
   */
  const mayFollowScenario = useCapability('scenarios') || !!value;
  /*
   * AND THE DRIVEN HALF HAS ITS OWN CAPABILITY — see `useDrivenAllowed`. It gates the DRIVERS
   * list for the same reason the line above gates the leaders: emptying the list is what keeps
   * the scenario half, the trigger and the restore path untouched.
   */
  const mayBeDriven = useDrivenAllowed(!!drivenBy);

  const leaders = !mayFollowScenario ? [] : (blocks ?? []).filter(b =>
    b.id !== selfId
    // DISPLAY ONLY, which the comment above has always said and the code did not check: a
    // Numerical with a question bank was being offered as a leader. §2.1 is the reason — an
    // input block's variants are question-and-answer PAIRS, so a leader that had its own
    // answer key would vary that key by its own versions while its followers varied theirs by
    // the same draw. Two selectors on one block.
    //
    // The complement of `ANSWERABLE_BLOCK_TYPES` rather than a second list of display types:
    // one of them would eventually not learn about a new block.
    && !ANSWERABLE_BLOCK_TYPES.includes(b.type as (typeof ANSWERABLE_BLOCK_TYPES)[number])
    && canLeadScenarios(b.data as { versions?: unknown[] })
    && !(b.data as { followsBlockId?: string }).followsBlockId);

  /*
   * A DRIVER IS AN EARLIER BLOCK WHOSE ANSWER SPACE PARTITIONS — see `drivenRowsFor`, which
   * owns that question so the menu and the rows cannot disagree about what is offerable.
   *
   * EARLIER MEANS THE GRAPH, not the array. This read `tasks.slice(0, here)` — every task
   * stored before this one — and the owner found what that costs: three sister pathways off one
   * split, reuniting, and only the sister that happened to be stored before the reunion was
   * offered. The other two sit AFTER it in the array and were silently absent, with no greyed
   * row to say they had been considered. Reported as "why can i link to forest arrival but not
   * any of the other pathways".
   *
   * `answerRefReach` is the question actually being asked — can a student pass through there and
   * then arrive here — and it is already what this file uses to decide whether a chosen driver
   * is total or conditional. Using it here as well means the menu and the status beneath it
   * cannot disagree about which tasks precede this one.
   *
   * AND UNREACHABLE IS GREYED, NOT DROPPED, which is this menu's rule throughout: "a greyed row
   * says the block was seen and refused; an absent one says nothing at all." `reachOfTask` below
   * already computes reach per task and the rows already read it, so a task no route reaches is
   * listed and disabled rather than filtered away here. Filtering it instead was the first
   * attempt and it broke exactly that — the row vanished and the teacher lost the reason.
   *
   * So the only task excluded outright is THIS one: a block cannot be driven by an answer given
   * in its own task, because that answer does not exist yet when the block renders.
   */
  const drivers = !onDrivenChange || !currentTaskId || !mayBeDriven ? [] : (tasks ?? [])
    .filter(tk => tk.id !== currentTaskId).flatMap(tk =>
    (tk.blocks ?? [])
      /*
       * EVERY EARLIER INPUT, not only the usable ones — the ineligible ones are shown greyed.
       *
       * Offering only what works answers "what can I pick" and leaves "why is my MCQ not
       * here" unanswerable, which is the question a teacher actually has when the block they
       * had in mind is missing. A greyed row says the block was seen and refused; an absent
       * one says nothing at all.
       *
       * INPUTS ONLY. A display block has no answer to vary with, so it is not refused — it is
       * not a candidate. `ANSWERABLE_BLOCK_TYPES` is the same list the scenario half uses to
       * exclude inputs from LEADING, read the other way round.
       */
      .filter(b => ANSWERABLE_BLOCK_TYPES.includes(b.type as (typeof ANSWERABLE_BLOCK_TYPES)[number]))
      /*
       * ONE ROW PER ANSWER SPACE, not per block — `driverSpacesFor` owns that difference, so
       * the menu never learns that FITB is the type with more than one. A passage with three
       * blanks is three things a link can point at, and offered as a single row there would
       * be no way to say which: `drivenRowsFor` refuses a FITB with no blank named, so the
       * block appeared, greyed, for ever.
       */
      .flatMap(b => driverSpacesFor(b.type, (b.data ?? {}) as Record<string, unknown>)
        .map(space => ({
          task: tk,
          block: b,
          space,
          /* Split by where the task sits, because "earlier" means something different on a
             pathway: a spine task is on every student's route and a pathway task is on some. */
          onSpine: !(tk as { pathwayOf?: string | null }).pathwayOf,
          eligible: space.eligible,
        }))));

  // Nothing to follow, nothing to be driven by, and neither already set: say nothing at all.
  // An orphaned block is the exception — it has a link to restore and no other way to do it.
  if (leaders.length === 0 && drivers.length === 0 && !value && !drivenBy && owner !== 'orphaned') {
    return null;
  }

  /* `BLOCK_META` rather than a `blocks.${type}.title` template key: the same table the library
     and the spine builder already name a block type from, and a looked-up key is one the i18n
     invariant can actually verify. */
  const nameOf = (b: Block) => b.name || (BLOCK_META[b.type] ? t(BLOCK_META[b.type]!.labelKey) : b.type);
  const glyphFor = (b: Block) => {
    const Icon = BLOCK_META[b.type]?.Icon;
    return Icon ? <Icon className='h-4 w-4' /> : null;
  };

  const NONE = '';
  const DRIVEN = 'driven:';
  const BLOCKED = 'blocked:';
  const linked = value ? leaders.find(b => b.id === value) : undefined;
  const addressOf = (d: typeof drivers[number]) =>
    formatDriverAddress({ kind: 'block', taskId: d.task.id, blockId: d.block.id, facet: d.space.facet });
  const driver = drivenBy ? drivers.find(d => addressOf(d) === drivenBy) : undefined;
  const address = parseDriverAddress(drivenBy);
  /* A faceted space is named by POSITION, because a blank has words and no label of its own —
     the same "Blank 2" the FITB editor and its issue messages already use. */
  const spaceSuffix = (d: typeof drivers[number]) => (d.space.facet
    ? ` \u00b7 ${t('blocks.fitb.blankLabel', { ordinal: d.space.index + 1 })}`
    : '');
  /*
   * The driver names its TASK as well as its block, truncated. A scenario can never be
   * cross-task, so a task prefix at all is what says which relationship this is — and the one
   * thing a teacher must not have to guess is whether a link reaches outside this task.
   */
  /*
   * BRACKETS, AND A MARK INSIDE THEM — the scope's distinction, drawn.
   *
   * A scenario link has no brackets and a driven one does: that IS how the two are told apart,
   * with no verb and no third glyph. The mark is the recall ladder, and it means more here
   * than on a chip — `?` says the driver sits on a pathway the student may not take, which the
   * Blank row is there to catch and is therefore informational; `×` says it cannot be reached
   * at all, which makes every version but Blank dead content.
   *
   * Leading only, as on a chip: `[? x ?]` is four glyphs of chrome around a short name.
   */
  const reach = drivenBy && tasks && currentTaskId
    ? drivenLinkReach(drivenBy, currentTaskId,
      tasks as Parameters<typeof drivenLinkReach>[2], scope?.aliases)
    : 'always';
  const reachMark = reach === 'sometimes' ? '? ' : reach === 'never' ? '\u00d7 ' : '';
  /*
   * AN ALIAS LINK NAMES THE ALIAS, which is the whole point of having made it.
   *
   * `driver` is looked up among the BLOCK rows, so an alias link found nothing there and the
   * trigger fell through to "Missing block" — on a link that was working perfectly, with its
   * seven versions on screen beside it. Found in the browser, not by any unit test, because
   * every test asserted the rows and none of them read the trigger.
   *
   * No task prefix: a task prefix exists to say which task a driver sits in, and an alias
   * deliberately spans several. The bracket and the reach mark are kept, so the three link
   * states still read alike.
   */
  const aliasDriver = address?.kind === 'alias'
    ? (scope?.aliases ?? []).find(a => a.id === address.aliasId) : undefined;
  const drivenLabel = driver
    ? `[${reachMark}${(driver.task.title ?? '').slice(0, 12)}\u2026 \u00b7 ${nameOf(driver.block)}${spaceSuffix(driver)}]`
    : aliasDriver
      ? `[${reachMark}${aliasDisplayName(aliasDriver, scope!, t)}]`
      : drivenBy ? t('blocks.scenario.drivenMissing')
        : owner === 'orphaned' ? t('blocks.scenario.driverLost') : '';
  /* What the trigger says: the leader's name for a scenario, the bracketed address for a
     driver, and nothing at all when neither — which collapses it to the glyph and its
     chevron, the quiet resting state this control has always had. */
  const triggerText = linked ? nameOf(linked) : drivenLabel;
  /*
   * THE SHARED PICKER, which this did not use and should have.
   *
   * It was a `SelectMenu`: no search, category headers smaller than the rows under them, and a
   * list that grows with every input block in every earlier task. Answer Recall and the flow
   * connection picker both use `FlowPickerMenu` — the same question asked of the same kind of
   * list — so a teacher met two different controls for "pick a block in this experience".
   *
   * It also carries `disabled` as a REASON rather than a flag, which is strictly better than
   * the boolean this had: a greyed row now says WHY, where before the block was simply dim and
   * the question "why is my MCQ not selectable" went unanswered on the row that raised it.
   */
  const sections: PickerSection[] = [];
  sections.push({ options: [{ key: NONE, text: t('blocks.scenario.linkNone'), selected: !value && !drivenBy }] });

  /*
   * WHAT AN ALIAS WOULD COST AND WHY IT MIGHT BE REFUSED — once per alias, not once per row.
   *
   * Every member of one alias offers the SAME link, so the version count, the formation faults
   * and the coverage gap are properties of the alias rather than of the row the teacher happens
   * to be looking at. Computed here so three member rows cannot disagree with each other.
   */
  /** A task id as a teacher reads it: the pathway it sits on, else the task's own title. */
  const routeNames = (taskIds: string[]) => {
    const seen = new Set<string>();
    for (const id of taskIds) {
      const tk = (tasks ?? []).find((x: { id: string }) => x.id === id) as
        { title?: string; pathwayName?: string } | undefined;
      const name = tk?.pathwayName || tk?.title;
      if (name) seen.add(name);
    }
    return [...seen].join(', ');
  };

  const aliasInfo = new Map<string, {
    name: string; count: number | null; disabled: string | undefined;
  }>();
  if (onDrivenChange && tasks && currentTaskId) {
    for (const a of scope?.aliases ?? []) {
      const space = aliasDriverSpace(
        a, tasks as Parameters<typeof aliasDriverSpace>[1],
        scope?.experienceVariableNames ?? new Set());
      const uncovered = aliasUncoveredFrom(
        a, currentTaskId, tasks as Parameters<typeof aliasUncoveredFrom>[2]);
      const fault = space.faults[0];
      aliasInfo.set(a.id, {
        name: aliasDisplayName(a, scope!, t),
        count: space.rows ? space.rows.length : null,
        disabled: fault
          ? (fault.kind === 'missing' ? t('blocks.scenario.aliasMemberMissing')
            : fault.kind === 'taskScoped'
              ? t('blocks.scenario.aliasMemberTaskScoped', { variable: fault.variable })
              : t(REFUSAL_REASON[fault.refusal as keyof typeof REFUSAL_REASON]))
          : uncovered.length
            /* `aliasUncoveredFrom` returns TASK IDS, deliberately — a pathway is a set of
               tasks and the teacher has to go to one of them. Joining the raw ids would have
               printed `t_c` at a teacher. */
            ? t('blocks.scenario.aliasNotTotal', { routes: routeNames(uncovered) })
            : undefined,
      });
    }
  }

  /*
   * WHICH ALIAS, IF ANY, SPEAKS FOR THIS ANSWER SPACE.
   *
   * A driver space has a `facet` only where the block answers in parts — FITB, one per blank —
   * and an alias member always names one, so a Numerical's bare space is matched against
   * `value`. Both sides spell a FITB blank with the blank's own id, so the two line up.
   */
  const aliasForSpace = (blockId: string, facet: string | undefined) =>
    (scope?.aliases ?? []).find(a =>
      /*
       * A NAME, OR MORE THAN ONE MEMBER — the recall picker's own rule, and the reason is the
       * same. An alias is minted the moment a recall chip is inserted, and an unnamed one's
       * display name is DERIVED from its only member: letting it take over the row printed
       * "Animal Test · MCQ 1" with "MCQ 1" repeated underneath, which is the block's address
       * twice and a name the teacher never gave. Nothing is lost — a single unnamed alias is
       * one block, and the row already IS that block.
       */
      (!!a.name || a.members.length > 1)
      && a.members.some(m => m.blockId === blockId && m.facet === (facet ?? ANSWER_FACET.value)));

  /*
   * ONE LIST, AND THE ALIAS IS THE NAME — the recall picker's shape, which the owner asked for
   * in as many words: "it's actually much better implemented in the [] and [ menu. there is no
   * shared category there, just all options, with alias listed first when present."
   *
   * The reason it reads better is that it states the rule instead of describing it. A separate
   * "Shared answers" section said *there is also a thing called critter*; a row under Forest
   * Pathway that READS "critter" says *this answer is critter* — "i know that if i pick critter,
   * even if it's from forest pathway, it will also be critter from beach pathway".
   *
   * So an aliased member row LINKS TO THE ALIAS, not to its own block. Three members are three
   * rows, under their three task headings, that make the same link; all three show as selected
   * once it is made, because all three are what the link means.
   */
  const driverOption = (d: typeof drivers[number]) => {
    const alias = aliasForSpace(d.block.id, d.space.facet);
    const info = alias ? aliasInfo.get(alias.id) : undefined;
    /* The block's own address, which becomes the SUB-LINE under an alias name and the whole
       label without one. No task prefix: the section heading above already says the task. */
    const own = `${nameOf(d.block)}${spaceSuffix(d)}`;
    const target = alias
      ? formatDriverAddress({ kind: 'alias', aliasId: alias.id })
      : addressOf(d);
    /* Unique per ROW while several rows share one target — the picker keys its options by
       this, and `onChoose` takes the half before the bar. */
    const key = `${DRIVEN}${target}|${d.block.id}:${d.space.facet ?? ''}`;
    /*
     * THE LINK GLYPH SAYS HOW OFTEN THIS GETS AN ANSWER, and for an alias it asks the MEMBERS
     * JOINTLY. Each member alone sits on a pathway and so is only `sometimes`; the three
     * together are reached always, and `drivenLinkReach` already knows the difference. Hence
     * the owner's one amendment: "if an alias has a complete coverage, change the partial link
     * to the full link icon".
     */
    const reach = currentTaskId && tasks
      ? drivenLinkReach(target, currentTaskId,
        tasks as Parameters<typeof drivenLinkReach>[2], scope?.aliases)
      : 'always';
    const ReachIcon = REACH_ICON[reach];
    return {
      key,
      /*
       * THE QUESTION IS SEARCHABLE, AND NOT SHOWN — `text` is what the filter matches and
       * `node` is what renders, which is the split the recall picker already uses for this.
       *
       * A teacher remembers the block by what it ASKED and by what it OFFERED, not by
       * "MCQ 2": names are theirs and six of them can be called Kayak. A question is often the
       * generic half — "Which of these?" — so the answers are what actually comes to mind.
       * Both summaries are imported rather than copied, because two strippers would disagree
       * the first time one learned a new inline element.
       */
      text: `${info?.name ?? ''} ${d.task.title ?? ''} ${own} ${questionSummary(d.block)} ${answerOptionSummary(d.block)}`,
      node: (
        <span className='flex min-w-0 flex-col'>
          <span className='flex min-w-0 items-baseline gap-2'>
            <span className='min-w-0 truncate'>{info?.name ?? own}</span>
            {/* THE COST, on the rows where it is a surprise. A block's own answer space is
                what the teacher is looking at; an alias's is the UNION of several, so four
                options plus six is nine versions and nothing else on screen says so. */}
            {info?.count !== undefined && info?.count !== null && (
              <span className='shrink-0 text-xs text-ws-muted'>
                {t('blocks.scenario.aliasVersionCount', { count: info.count })}
              </span>
            )}
          </span>
          {/* What this row IS, under what it is called — the recall picker's own treatment of
              a named answer, so the two menus teach the shape once. */}
          {info && (
            <span className='truncate text-[0.6875rem] italic leading-tight text-ws-muted'>{own}</span>
          )}
        </span>
      ),
      icon: (
        <span className='flex shrink-0 items-center gap-1 self-center'>
          {glyphFor(d.block)}
          <ReachIcon aria-hidden='true' className='h-3 w-3 opacity-70' />
          <span className='sr-only'>{t(REACH_LABEL[reach])}</span>
        </span>
      ),
      /*
       * AND A LINK STORED BEFORE THE ROW WAS NAMED still reads as chosen.
       *
       * An aliased row now links to the ALIAS, but a link made when the menu offered the block
       * holds the block's own address. Matching only the new form would show "nothing chosen"
       * on a block that is perfectly linked, and the trigger beside it saying otherwise. It is
       * rewritten to the alias form the moment the teacher picks the row, which is a change
       * they asked for rather than one made behind them.
       */
      selected: target === drivenBy || addressOf(d) === drivenBy,
      /*
       * TWO WAYS A ROW CAN BE UNAVAILABLE, and they are different questions. The refusal is
       * about the BLOCK — its answer does not partition. Unreachable is about the ROUTE — the
       * answer would partition fine and no student here can have given one. Refusal first
       * because it is the property of the thing itself.
       *
       * An ALIASED row answers with the alias's reason throughout: every member offers the
       * same link, so a row refused for its own block's sake while the link it makes is fine
       * would be refusing the wrong thing.
       */
      disabled: info
        ? info.disabled
        : d.space.refusal
          ? t(REFUSAL_REASON[d.space.refusal])
          : reach === 'never'
            ? t('blocks.scenario.unreachableHere')
            : undefined,
    };
  };

  if (leaders.length > 0) {
    sections.push({
      /* A scenario leader can only ever be in THIS task — that is what separates it
         from a driver, so the heading says it rather than naming the mechanic. */
      header: t('blocks.scenario.groupThisTask'),
      options: blockedScenario
        /* One row for the whole section, not the same sentence on every leader — the reason is
           a property of THIS block, not of any one thing it could have followed. */
        ? [{ key: `${BLOCKED}scenario`, text: t(BLOCKED_REASON[blockedScenario]),
          disabled: t(BLOCKED_REASON[blockedScenario]) }]
        : leaders.map(b => ({
          key: b.id, text: nameOf(b), icon: glyphFor(b), selected: b.id === value,
        })),
    });
  }

  /*
   * ONE SECTION PER TASK, spine first and then the pathways in the order they hang off it —
   * the recall picker's own ordering, because it answers the same question about the same
   * graph and a teacher should not have to learn two layouts for "pick an earlier answer".
   *
   * It was "Main flow" and then one section per PATHWAY, which lumped every spine task under
   * one heading. Naming the TASK is what lets an aliased row say `critter` on its own line:
   * the heading carries the address's other half, so the row is free to carry the name.
   */
  if (drivers.length > 0) {
    if (blockedDriven) {
      /* One row for the whole list, not the same sentence on every driver — the reason is a
         property of THIS block, not of any one thing it could have been driven by. */
      sections.push({
        options: [{ key: `${BLOCKED}driven`, text: t(BLOCKED_REASON[blockedDriven]),
          disabled: t(BLOCKED_REASON[blockedDriven]) }],
      });
    } else {
      const graph = deriveGraph(tasks as Parameters<typeof deriveGraph>[0]);
      const onSpine = new Set(graph.spine);
      const rank = (taskId: string) => (onSpine.has(taskId)
        ? graph.spine.indexOf(taskId)
        : /* After every spine task, ordered by where the task they detour off SITS on it —
             the anchor is what a teacher looks for a pathway near. */
        graph.spine.length + graph.spine.indexOf(
          (tasks ?? []).find(tk => tk.id === taskId)?.pathwayOf ?? ''));
      const byTask = new Map<string, typeof drivers>();
      for (const d of drivers) {
        const got = byTask.get(d.task.id);
        if (got) got.push(d); else byTask.set(d.task.id, [d]);
      }
      for (const [taskId, rows] of [...byTask].sort((a, b) => rank(a[0]) - rank(b[0]))) {
        const task = rows[0]!.task as { title?: string; pathwayName?: string };
        const pathway = onSpine.has(taskId) ? null
          : task.pathwayName || t('experience.builder.pathwayUnnamed');
        sections.push({
          /* The teacher's own names, so they keep their capitalisation — the same reason a
             variable set's header does. */
          headerIsName: true,
          header: pathway ? `${task.title ?? ''} · ${pathway}` : (task.title ?? ''),
          options: rows.map(driverOption),
        });
      }
    }
  }

  return (
    <FlowPickerMenu
      sections={sections}
      ariaLabel={t('blocks.scenario.linkLabel')}
      /* Searchable, because this list is every input block in every earlier task and grows
         with the experience — the case `filterPlaceholder` exists for. */
      filterPlaceholder={t('blocks.scenario.linkFilter')}
      noMatchesLabel={t('blocks.scenario.linkNoMatches')}
      onChoose={v => {
        if (v.startsWith(DRIVEN)) {
          if (value) onChange(undefined);
          /* The bar separates the TARGET from the row that offered it: an alias is offered once
             per member, so three rows make one link and need three keys. Neither a block
             address (`task:block:facet`) nor `alias:<id>` can contain a bar. */
          onDrivenChange?.(v.slice(DRIVEN.length).split('|')[0]);
          return;
        }
        /*
         * NONE LETS GO OF THE WHOLE AXIS, not just the link — and RETURNS rather than falling
         * through. Clearing `drivenBy` alone left the versions carrying their row ids, so the
         * block read as `orphaned` and refused every other choice; and `onChange` builds its
         * payload from the block data as it was BEFORE this write, so clearing a scenario link
         * that was never set put the driven link straight back.
         *
         * The scenario half needs no equivalent collapse: a follower's bank cannot survive the
         * link (§2.2), so `linkToLeader(undefined)` has only `scenarioBranches` to drop.
         */
        if (v === NONE) {
          /*
           * ORPHANED COUNTS AS DRIVEN HERE, or the escape hatch has no exit.
           *
           * `orphaned` is versions still carrying their row ids with no driver above them —
           * the state a block lands in when the link goes and the bank does not. Guarding the
           * collapse on `drivenBy` alone meant None did nothing at all there, so the teacher
           * met "Link lost", pressed the one control that means "stop varying", and watched it
           * change nothing. Reported as "i set it to none, it should still be at none with the
           * driven by collection of questions/answers removed, leaving just the first one,
           * when i come back".
           *
           * `collapseDrivenAxis` needs no driver: `releaseDriver` is a no-op when there is
           * none, and the fold is the part that matters.
           */
          if ((drivenBy || owner === 'orphaned') && emit) {
            /* COLLAPSES the bank, as unlinking a scenario and unticking branching corrects both
               do with theirs — version one is folded back onto the block and the alternatives
               go. One emit, so one undo brings the whole axis back. */
            emit(collapseDrivenAxis(
              block.data as Parameters<typeof collapseDrivenAxis>[0],
              applyVersion as Parameters<typeof collapseDrivenAxis>[1],
            ));
          }
          if (value) onChange(undefined);
          return;
        }
        if (drivenBy) onDrivenChange?.(undefined);
        onChange(v);
      }}
      renderTrigger={p => (
        <button
          {...p}
          type='button'
          aria-label={t('blocks.scenario.linkLabel')}
          className='flex min-w-0 items-center gap-1 rounded border border-ws-border bg-ws-btn px-2 py-1 text-left text-xs text-ws-text hover:bg-ws-hover focus:border-ws-primary focus:outline-none'
        >
          {/*
            * ANCHOR for a scenario, LINK for a driver — the canvas pills' vocabulary, said by
            * the control that sets them. The owner asked for it here too, and the pair carries
            * more than the two words: a scenario names a block in THIS task and a driver is
            * always in an earlier one, so the glyph is also the only thing that has to say
            * whether this reaches outside the task.
            *
            * `Link2` is a plain chain. `Waypoints` was tried first on the theory that a chain
            * reads as "hyperlink" beside a rich-text toolbar; in place its connected dots read
            * as random lightning instead, which is worse.
            */}
          {owner === 'orphaned'
            ? <OctagonAlert aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-error' />
            : owner === 'scenario'
              ? <Anchor aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />
              : <Link2 aria-hidden='true' className='h-3.5 w-3.5 shrink-0' />}
          {triggerText && <span className='min-w-0 truncate'>{triggerText}</span>}
          <ChevronDown className='h-3 w-3 shrink-0 opacity-60' aria-hidden='true' />
        </button>
      )}
    />
  );
}
